import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import { PlansResponse } from '../../types';
import { showSnackbar } from '../../store/reducers/snackbarSlice';
import ConfirmModal from '../ConfirmationModal/ConfirmationModal';
import CompanyForm from '../Company/СompanyForm/CompanyForm';
import PushPanel from '../PushPanel/PushPanel';
import NewPlanModal from './NewPlanModal/NewPlanModal';
import PlanActionsModal from './PlanActionsModal/PlanActionsModal';
import PlanDefinitionsModal from './PlanDefinitionsModal/PlanDefinitionsModal';
import { getBackgroundClass } from './helpers';
import { usePlanModal } from './hooks/usePlanModal';
import SortingAZ from './icons/SortingAZ';
import SortingZA from './icons/SortingZA';
import NonAvatar from './icons/nonAvatar';
import Sharpens from './icons/sharpens';
import style from './plan.module.css';
import {
  fetchClientsPlanRequestsList,
  fetchClientsPlansList,
  removeClientPlan,
  removePlanRequest,
} from './planService';
import { PlanType } from './planTypes';

const PlanComponent: React.FC = () => {
  const [clientsPlans, setClientsPlans] = useState<any[]>([]);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [activeType, setActiveType] = useState<'active' | 'completed'>(
    'active',
  );
  const [isAscending, setIsAscending] = useState<boolean>(true);
  const [isSortingActive, setIsSortingActive] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [needsScroll, setNeedsScroll] = useState<boolean>(false);
  const [isNewPlanModalOpen, setIsNewPlanModalOpen] = useState<boolean>(false);
  const [isPlanDefsOpen, setIsPlanDefsOpen] = useState<boolean>(false); // #29
  const [selectedPlanForEdit, setSelectedPlanForEdit] = useState<any | null>(
    null,
  );
  const [confirmState, setConfirmState] = useState<{
    kind: 'deactivate' | 'reject';
    plan: any;
  } | null>(null);
  const [isMutating, setIsMutating] = useState<boolean>(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useRef<HTMLDivElement | null>(null);
  const displayPlanRef = useRef<HTMLDivElement | null>(null);
  const {
    modalPosition,
    selectedPlanId,
    canMoveModal,
    handleMenuButtonClick,
    handleCloseModal,
  } = usePlanModal();

  const isRequestInProgress = useRef(false);
  const dispatch = useDispatch();

  const fetchPlans = useCallback(async () => {
    if (isRequestInProgress.current) return;
    isRequestInProgress.current = true;

    setIsFetching(true);
    try {
      let data: PlansResponse;

      if (activeType === 'active') {
        data = await fetchClientsPlansList(
          pageIndex,
          12,
          searchQuery,
          isSortingActive ? (isAscending ? 'asc' : 'desc') : '',
        );
      } else {
        data = await fetchClientsPlanRequestsList(
          pageIndex,
          12,
          searchQuery,
          isSortingActive ? (isAscending ? 'asc' : 'desc') : '',
        );
      }

      if (pageIndex === 1) {
        setClientsPlans(data.items);
      } else {
        setClientsPlans((prevPlans) => [...prevPlans, ...data.items]);
      }

      setHasMore(data.items.length >= 12);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setIsFetching(false);
      isRequestInProgress.current = false;
    }
  }, [activeType, pageIndex, isAscending, searchQuery, isSortingActive]);

  useEffect(() => {
    setPageIndex(1);
    fetchPlans();
  }, [activeType, isAscending, searchQuery]);

  const handleActiveClick = () => {
    setActiveType('active');
  };

  const handleCompletedClick = () => {
    setActiveType('completed');
  };

  const handleSortClick = () => {
    setIsSortingActive(true);
    setIsAscending((prevIsAscending) => !prevIsAscending);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      setSearchQuery(query);
    }, 1000),
    [],
  );

  useEffect(() => {
    if (searchQuery) {
      debouncedSearch(searchQuery);
    }
  }, [searchQuery]);

  useEffect(() => {
    const checkIfNeedsScroll = () => {
      if (displayPlanRef.current) {
        const containerHeight = displayPlanRef.current.clientHeight;
        const contentHeight = displayPlanRef.current.scrollHeight;
        setNeedsScroll(contentHeight > containerHeight + 1);
      }
    };

    checkIfNeedsScroll();
    window.addEventListener('resize', checkIfNeedsScroll);

    return () => {
      window.removeEventListener('resize', checkIfNeedsScroll);
    };
  }, [clientsPlans]);

  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !isFetching) {
        setPageIndex((prevPageIndex) => prevPageIndex + 1);
      }
    },
    [hasMore, isFetching],
  );

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    });

    if (lastElementRef.current && hasMore) {
      observerRef.current.observe(lastElementRef.current);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [clientsPlans, observerCallback, hasMore]);

  const handleClearSearch = () => {
    setSearchQuery('');
    setClientsPlans([]);
  };

  const noResultsMessage =
    searchQuery && clientsPlans.length === 0
      ? 'Співпадінь не знайдено. Спробуйте іншу назву, щоб розпочати роботу.'
      : '';

  const noPlansMessage =
    activeType === 'active' && clientsPlans.length === 0
      ? 'Немає активних тарифів.'
      : activeType === 'completed' && clientsPlans.length === 0
        ? 'Вхідних заявок наразі немає.'
        : '';

  const handleEditPlan = (planId: string) => {
    const selectedPlan = clientsPlans.find(
      (plan) => plan.id === planId || plan.requestId === planId,
    );

    if (selectedPlan?.userId === null) {
      setSelectedPlanForEdit(selectedPlan);
      setIsNewPlanModalOpen(true);
    } else if (selectedPlan?.userId) {
      setSelectedPlanForEdit(selectedPlan);
      setIsNewPlanModalOpen(true);
    } else {
      console.error('Plan not found for editing');
    }

    handleCloseModal();
  };

  const handleTogglePlanStatus = (planId: string) => {
    const selectedPlan = clientsPlans.find(
      (plan) => plan.id === planId || plan.requestId === planId,
    );
    if (!selectedPlan) {
      console.error('Plan not found for status toggle');
      handleCloseModal();
      return;
    }
    // Вкладка «Заявки» → відхилення заявки; «Активні» → деактивація тарифу.
    setConfirmState({
      kind: activeType === 'completed' ? 'reject' : 'deactivate',
      plan: selectedPlan,
    });
    handleCloseModal();
  };

  const handleSavePlan = (updatedPlan: any) => {
    setIsNewPlanModalOpen(false);
  };

  const handleCloseNewPlanModal = () => {
    setIsNewPlanModalOpen(false);
  };

  const handleUpdatePlans = () => {
    setPageIndex(1);
    fetchPlans();
  };

  const performToggle = async () => {
    if (!confirmState || isMutating) return;
    const { kind, plan } = confirmState;
    setIsMutating(true);
    try {
      if (kind === 'reject') {
        await removePlanRequest(plan.requestId);
        dispatch(
          showSnackbar({ message: 'Заявку відхилено', type: 'success' }),
        );
      } else {
        await removeClientPlan(plan.id || plan.userId);
        dispatch(
          showSnackbar({ message: 'Тариф деактивовано', type: 'success' }),
        );
      }
      handleUpdatePlans();
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Сталася помилка. Спробуйте ще раз.',
          type: 'error',
        }),
      );
    } finally {
      setIsMutating(false);
      setConfirmState(null);
    }
  };

  const loadMorePlans = () => {
    setPageIndex((prevPageIndex) => prevPageIndex + 1);
    fetchPlans();
  };

  useEffect(() => {
    if (pageIndex > 1) {
      fetchPlans();
    }
  }, [pageIndex]);

  useEffect(() => {
    setClientsPlans([]);
    setPageIndex(1);
    setHasMore(true);
    fetchPlans();
  }, [activeType, isAscending, searchQuery]);

  return (
    <div className={style.wrapPlan}>
      <PushPanel
        onActiveClick={handleActiveClick}
        onCompletedClick={handleCompletedClick}
        activeType={activeType}
        onSearch={handleSearch}
        onClear={handleClearSearch}
      />

      {/* #29: керування визначеннями тарифів */}
      <div
        style={{ display: 'flex', justifyContent: 'flex-end', margin: '8px 0' }}
      >
        <button
          type="button"
          onClick={() => setIsPlanDefsOpen(true)}
          style={{
            padding: '8px 16px',
            borderRadius: 10,
            border: 'none',
            cursor: 'pointer',
            fontWeight: 600,
            background: '#0a8081',
            color: '#ffffff',
          }}
        >
          Керувати тарифами
        </button>
      </div>

      {isPlanDefsOpen && (
        <PlanDefinitionsModal onClose={() => setIsPlanDefsOpen(false)} />
      )}

      <div
        id="scrollableDivPlan"
        className={`${style.displayPlan} ${needsScroll ? '' : style.borderBottomRadius}`}
        ref={displayPlanRef}
      >
        <InfiniteScroll
          dataLength={clientsPlans.length}
          next={loadMorePlans}
          hasMore={hasMore}
          scrollableTarget="scrollableDivPlan"
          scrollThreshold={0.8}
          loader={''}
        >
          <div className={style.plansTitles}>
            <h5>#</h5>
            <div className={style.sortName}>
              <h5>НАЗВА Компанії</h5>
              <button onClick={handleSortClick} className={style.sortButton}>
                {isAscending ? <SortingAZ /> : <SortingZA />}
              </button>
            </div>
            <h5>Тариф</h5>
          </div>
          <div className={style.scrollPlan}>
            {noPlansMessage && (
              <p className={style.noPlansMessage}>{noPlansMessage}</p>
            )}
            {noResultsMessage && !noPlansMessage && (
              <p className={style.noResultsMessage}>{noResultsMessage}</p>
            )}
            {!noResultsMessage &&
              !noPlansMessage &&
              clientsPlans.map((plan, index) => {
                const planId =
                  activeType === 'completed' ? plan.requestId : plan.id;

                return (
                  <div
                    key={`${planId}-${index}`}
                    className={style.planItem}
                    ref={
                      index === clientsPlans.length - 1 ? lastElementRef : null
                    }
                  >
                    <h5>{index + 1}</h5>
                    <div className={style.infoWrap}>
                      <div className={style.avatarWrap}>
                        {plan.avatarUrl ? (
                          <img src={plan.avatarUrl} alt={plan.userName} />
                        ) : (
                          <NonAvatar />
                        )}
                      </div>
                      <h5>
                        {/* {activeType === 'active' ? plan.userName : plan.name} */}
                        {activeType === 'active'
                          ? plan.companyName
                            ? plan.companyName.length > 80
                              ? `${plan.companyName.slice(0, 80)}...`
                              : plan.companyName
                            : ''
                          : plan.name
                            ? plan.name.length > 30
                              ? `${plan.name.slice(0, 30)}...`
                              : plan.name
                            : ''}
                      </h5>
                    </div>
                    <div className={style.wrapDisplayPlan}>
                      <h5
                        className={`${style.planStyle} ${getBackgroundClass(
                          (plan.plan as PlanType) || (plan.newPlan as PlanType),
                        )}`}
                      >
                        {plan.plan || plan.newPlan}
                      </h5>
                    </div>
                    <button
                      className={style.menuButton}
                      onClick={(e) => handleMenuButtonClick(e, planId)}
                    >
                      <Sharpens />
                    </button>
                  </div>
                );
              })}
          </div>
        </InfiniteScroll>
      </div>

      {modalPosition && selectedPlanId && (
        <PlanActionsModal
          isActive={activeType === 'completed'}
          onEdit={() => handleEditPlan(selectedPlanId)}
          onToggleStatus={() => handleTogglePlanStatus(selectedPlanId)}
          onClose={handleCloseModal}
          position={modalPosition}
          canMove={canMoveModal}
          selectedPlan={clientsPlans.find(
            (plan) =>
              plan.id === selectedPlanId || plan.requestId === selectedPlanId,
          )}
        />
      )}
      {isNewPlanModalOpen && selectedPlanForEdit?.userId === null ? (
        <CompanyForm
          companyData={selectedPlanForEdit}
          formDataArray={selectedPlanForEdit?.fields || []}
          onClose={handleCloseNewPlanModal}
          onSave={(savedCompany) => {
            handleUpdatePlans();
            handleCloseNewPlanModal();
          }}
          isEdit={false}
          requestPlanId={selectedPlanForEdit?.requestId}
        />
      ) : (
        <NewPlanModal
          isOpen={isNewPlanModalOpen}
          selectedPlan={selectedPlanForEdit}
          onSave={handleSavePlan}
          onClose={handleCloseNewPlanModal}
        />
      )}

      {confirmState && (
        <ConfirmModal
          type="Company"
          userName={confirmState.plan?.companyName || confirmState.plan?.name}
          avatarUrl={confirmState.plan?.avatarUrl}
          title={
            confirmState.kind === 'reject'
              ? 'Відхилення заявки'
              : 'Деактивація тарифу'
          }
          message={
            confirmState.kind === 'reject'
              ? 'Ви впевнені, що хочете відхилити цю заявку на тариф?'
              : 'Ви впевнені, що хочете деактивувати тариф цього клієнта?'
          }
          confirmLabel={
            confirmState.kind === 'reject' ? 'Відхилити' : 'Деактивувати'
          }
          cancelLabel="Скасувати"
          onConfirm={performToggle}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
};

export default PlanComponent;
